import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { rm } from "../../lib/format";
import { toast } from "../../stores/toast";
import { Button, Input, Select, Textarea } from "../ui";
import { RightSidebarPanel } from "../RightSidebarPanel";
import { SearchSelect } from "../SearchSelect";
import { useInfiniteCatalogProducts } from "../../hooks/useInfiniteCatalogProducts";

type LineDraft = {
  key: string;
  productId: string;
  name: string;
  quantity: string;
  unitPrice: string;
};

function emptyLine(): LineDraft {
  return { key: Math.random().toString(36).slice(2), productId: "", name: "", quantity: "1", unitPrice: "" };
}

export function ManualOrderCreatePanel({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (orderId: string) => void;
}) {
  const [customerMode, setCustomerMode] = useState<"user" | "custom">("user");
  const [userId, setUserId] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [customName, setCustomName] = useState("");
  const [customEmail, setCustomEmail] = useState("");
  const [customPhone, setCustomPhone] = useState("");
  const [lines, setLines] = useState<LineDraft[]>(() => [emptyLine()]);
  const [productSearch, setProductSearch] = useState("");
  const [shippingFee, setShippingFee] = useState("0");
  const [initialStatus, setInitialStatus] = useState<"PENDING_PAYMENT" | "PAID">("PAID");
  const [notes, setNotes] = useState("");
  const [reserveStock, setReserveStock] = useState(true);

  const usersQuery = useQuery({
    queryKey: ["users-picker", userSearch],
    queryFn: () =>
      api<{ items: Array<{ id: string; email: string; displayName: string | null }> }>(
        `/users?page=1&pageSize=30${userSearch.trim() ? `&search=${encodeURIComponent(userSearch.trim())}` : ""}`,
      ),
    enabled: open && customerMode === "user",
  });

  const catalogQuery = useInfiniteCatalogProducts({
    enabled: open,
    search: productSearch,
    queryKey: ["manual-order-products"],
    params: { active: "true" },
  });

  const userOptions = useMemo(
    () =>
      (usersQuery.data?.items ?? []).map((u) => ({
        value: u.id,
        label: u.displayName ? `${u.displayName} · ${u.email}` : u.email,
      })),
    [usersQuery.data?.items],
  );

  const productOptions = useMemo(
    () =>
      catalogQuery.items.map((p) => ({
        value: p.id,
        label: p.sku ? `${p.name} (${p.sku})` : p.name,
        hint: p.price != null ? rm(p.price) : undefined,
        image: p.images?.[0] ?? null,
      })),
    [catalogQuery.items],
  );

  const merchandiseTotal = useMemo(() => {
    return lines.reduce((sum, line) => {
      const q = Number.parseInt(line.quantity, 10);
      const p = Number.parseFloat(line.unitPrice);
      if (!Number.isFinite(q) || !Number.isFinite(p)) return sum;
      return sum + q * p;
    }, 0);
  }, [lines]);

  const shipping = Number.parseFloat(shippingFee) || 0;
  const grandTotal = merchandiseTotal + shipping;

  const createMut = useMutation({
    mutationFn: () => {
      const parsedLines = lines.map((line) => {
        const quantity = Number.parseInt(line.quantity, 10);
        const unitPrice = Number.parseFloat(line.unitPrice);
        if (!Number.isFinite(quantity) || quantity < 1) throw new Error("Invalid quantity on a line");
        if (!Number.isFinite(unitPrice) || unitPrice < 0) throw new Error("Invalid price on a line");
        if (!line.productId && !line.name.trim()) throw new Error("Each line needs a product or description");
        return {
          productId: line.productId || undefined,
          name: line.name.trim(),
          quantity,
          unitPrice,
        };
      });
      return api<{ order: { id: string } }>("/orders/manual", {
        method: "POST",
        body: {
          userId: customerMode === "user" ? userId || undefined : undefined,
          customer:
            customerMode === "custom"
              ? { name: customName.trim(), email: customEmail.trim(), phone: customPhone.trim() || undefined }
              : undefined,
          lines: parsedLines,
          shippingFee: shipping,
          initialStatus,
          notes: notes.trim() || undefined,
          reserveStock,
        },
      });
    },
    onSuccess: (res) => {
      toast("success", initialStatus === "PAID" ? "Manual order created and marked paid" : "Manual order created");
      onCreated(res.order.id);
      onClose();
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Create failed"),
  });

  function addProductLine(productId: string) {
    const product = catalogQuery.items.find((p) => p.id === productId);
    if (!product) return;
    setLines((prev) => [
      ...prev,
      {
        key: Math.random().toString(36).slice(2),
        productId,
        name: product.name,
        quantity: "1",
        unitPrice: product.price != null ? String(product.price) : "0",
      },
    ]);
  }

  return (
    <RightSidebarPanel
      open={open}
      onClose={onClose}
      title="Create manual order"
      wide
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={createMut.isPending || (customerMode === "user" ? !userId : !customName.trim() || !customEmail.trim())}
            onClick={() => createMut.mutate()}
          >
            Create
          </Button>
        </div>
      }
    >
      <div className="space-y-6 text-sm">
        <section className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">Customer</p>
          <div className="flex gap-2">
            <Button variant={customerMode === "user" ? "primary" : "outline"} size="sm" onClick={() => setCustomerMode("user")}>
              Existing user
            </Button>
            <Button variant={customerMode === "custom" ? "primary" : "outline"} size="sm" onClick={() => setCustomerMode("custom")}>
              Custom details
            </Button>
          </div>
          {customerMode === "user" ? (
            <SearchSelect
              label="User"
              placeholder="Search name or email"
              value={userId}
              onChange={setUserId}
              options={userOptions}
              onSearchChange={setUserSearch}
              emptyMessage={usersQuery.isLoading ? "Loading…" : undefined}
            />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Name" value={customName} onChange={(e) => setCustomName(e.target.value)} />
              <Input label="Email" type="email" value={customEmail} onChange={(e) => setCustomEmail(e.target.value)} />
              <Input label="Phone" className="sm:col-span-2" value={customPhone} onChange={(e) => setCustomPhone(e.target.value)} />
            </div>
          )}
        </section>

        <section className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">Line items</p>
          <SearchSelect
            label="Add warehouse product"
            placeholder="Search catalog"
            value=""
            onChange={(id) => {
              if (id) addProductLine(id);
            }}
            options={productOptions}
            onSearchChange={setProductSearch}
            onLoadMore={catalogQuery.fetchNextPage}
            hasMore={catalogQuery.hasNextPage}
            loadingMore={catalogQuery.isFetchingNextPage}
            emptyMessage={catalogQuery.isLoading ? "Loading…" : undefined}
          />
          <ul className="space-y-3">
            {lines.map((line, idx) => (
              <li key={line.key} className="rounded-xl border border-line bg-surface-2/50 p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-muted">Item {idx + 1}</span>
                  {lines.length > 1 ? (
                    <button
                      type="button"
                      className="rounded-lg p-1 text-muted hover:bg-hover hover:text-foreground"
                      aria-label="Remove line"
                      onClick={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}
                    >
                      <Trash2 size={14} />
                    </button>
                  ) : null}
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Input
                    label="Description"
                    value={line.name}
                    onChange={(e) =>
                      setLines((prev) => prev.map((l) => (l.key === line.key ? { ...l, name: e.target.value } : l)))
                    }
                    hint={line.productId ? "Linked to catalog product" : "Custom line"}
                  />
                  <Input
                    label="Qty"
                    inputMode="numeric"
                    value={line.quantity}
                    onChange={(e) =>
                      setLines((prev) => prev.map((l) => (l.key === line.key ? { ...l, quantity: e.target.value } : l)))
                    }
                  />
                  <Input
                    label="Unit price (MYR)"
                    inputMode="decimal"
                    className="sm:col-span-2"
                    value={line.unitPrice}
                    onChange={(e) =>
                      setLines((prev) => prev.map((l) => (l.key === line.key ? { ...l, unitPrice: e.target.value } : l)))
                    }
                  />
                </div>
              </li>
            ))}
          </ul>
          <Button variant="outline" size="sm" onClick={() => setLines((prev) => [...prev, emptyLine()])}>
            <Plus className="mr-1 inline h-4 w-4" />
            Add custom line
          </Button>
        </section>

        <section className="grid gap-3 sm:grid-cols-2">
          <Input label="Shipping fee (MYR)" inputMode="decimal" value={shippingFee} onChange={(e) => setShippingFee(e.target.value)} />
          <Select
            label="Initial status"
            value={initialStatus}
            onChange={(e) => setInitialStatus(e.target.value as "PENDING_PAYMENT" | "PAID")}
          >
            <option value="PAID">Paid</option>
            <option value="PENDING_PAYMENT">Pending payment</option>
          </Select>
        </section>

        <label className="flex items-center gap-2 text-xs text-muted">
          <input type="checkbox" checked={reserveStock} onChange={(e) => setReserveStock(e.target.checked)} />
          Reserve warehouse stock for catalog lines
        </label>

        <Textarea label="Notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />

        <div className="rounded-xl border border-line bg-surface-2/40 p-4 text-sm">
          <div className="flex justify-between">
            <span className="text-muted">Merchandise</span>
            <span>{rm(merchandiseTotal)}</span>
          </div>
          <div className="mt-1 flex justify-between">
            <span className="text-muted">Shipping</span>
            <span>{rm(shipping)}</span>
          </div>
          <div className="mt-2 flex justify-between font-display font-bold text-brand-pink">
            <span>Total</span>
            <span>{rm(grandTotal)}</span>
          </div>
        </div>
      </div>
    </RightSidebarPanel>
  );
}
